import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useParams, Navigate, useSearchParams, useLocation } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { CheckCircle } from "lucide-react";
import { Toaster } from "sonner";
import Home from "@/pages/Home";
import Contact from "@/pages/Contact";
import Packages from "@/pages/Packages";
import Map from "@/pages/Map";
import PartnerDiveCenters from "@/pages/PartnerDiveCenters";
import Gallery from "@/pages/Gallery";
import Reviews from "@/pages/Reviews";
import Account from "@/pages/Account";
import Admin from "@/pages/Admin";
import Register from "@/pages/Register";
import Profile from "@/pages/Profile";
import SubmitReview from "@/pages/SubmitReview";
import SubmitDiveSiteReport from "@/pages/SubmitDiveSiteReport";
import DiveReportForm from "@/pages/DiveReportForm";
import VerifyEmail from "@/pages/VerifyEmail";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import PrivacySettings from "@/pages/PrivacySettings";
import Accessibility from "@/pages/Accessibility";
import Community from "@/pages/Community";
import ProsDirectory from "@/pages/ProsDirectory";
import DiveCentersDirectory from "@/pages/DiveCentersDirectory";
import Forum from "@/pages/Forum";
import ThreadDetail from "@/pages/ThreadDetail";
import RequestInvite from "@/pages/RequestInvite";
import MarineDatabase from "@/pages/MarineDatabase";
import Navigation from "@/components/Navigation";
import CookieConsent from "@/components/CookieConsent";
import ScrollToTop from "@/components/ScrollToTop";
import FloatingChat from "@/components/FloatingChat";
import NewUserWalkthrough from "@/components/NewUserWalkthrough";
import { ThemeProvider, useTheme } from "@/contexts/ThemeContext";
import usePushNotifications from "@/hooks/usePushNotifications";
import { BACKEND_URL } from '@/utils/apiConfig';

const API = `${BACKEND_URL}/api`;

// Redirect /review?token=xxx to /submit-review?token=xxx
const ReviewRedirect = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  return <Navigate to={token ? `/submit-review?token=${token}` : '/submit-review'} replace />;
};

// Prevent Radix UI from modifying body styles (causes layout shift)
const usePreventBodyStyleChanges = () => {
  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes') {
          const body = document.body;
          // Remove data-scroll-locked attribute set by react-remove-scroll-bar
          // This attribute triggers injected CSS: body[data-scroll-locked] { overflow: hidden !important }
          if (mutation.attributeName === 'data-scroll-locked' && body.hasAttribute('data-scroll-locked')) {
            body.removeAttribute('data-scroll-locked');
          }
          // Also catch any inline style overrides
          if (mutation.attributeName === 'style') {
            if (body.style.paddingRight || body.style.marginRight || body.style.overflow === 'hidden') {
              body.style.paddingRight = '';
              body.style.marginRight = '';
              body.style.overflow = '';
            }
          }
        }
      });
    });

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['style', 'data-scroll-locked']
    });

    return () => observer.disconnect();
  }, []);
};

// Global FloatingChat wrapper - shows for logged-in users except dive_center
const GlobalFloatingChat = () => {
  const [user, setUser] = useState(null);

  useEffect(() => {
    const userData = localStorage.getItem("user") || sessionStorage.getItem("user");
    if (userData) {
      try {
        const parsedUser = JSON.parse(userData);
        if (parsedUser.role !== "dive_center") {
          setUser(parsedUser);
        }
      } catch (e) {
        setUser(null);
      }
    }

    // Listen for login/logout events
    const handleStorageChange = () => {
      const userData = localStorage.getItem("user") || sessionStorage.getItem("user");
      if (userData) {
        try {
          const parsedUser = JSON.parse(userData);
          if (parsedUser.role !== "dive_center") {
            setUser(parsedUser);
          } else {
            setUser(null);
          }
        } catch (e) {
          setUser(null);
        }
      } else {
        setUser(null);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('userLogin', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userLogin', handleStorageChange);
    };
  }, []);

  if (!user) return null;
  return <FloatingChat />;
};

const ProgramDetail = () => {
  const { programId } = useParams();
  const navigate = useNavigate();
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProgram = async () => {
      try {
        const response = await axios.get(`${API}/intro-programs/${programId}`);
        setProgram(response.data);
      } catch (error) {
        console.error("Error loading program:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProgram();
  }, [programId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-blue-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  if (!program) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-blue-900 flex items-center justify-center">
        <div className="text-white text-xl">Program not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-blue-900">
      <Navigation />

      {/* Program Details */}
      <div className="container mx-auto px-4 py-16 max-w-4xl">
        <Card className="bg-white/5 backdrop-blur-md border-white/10 text-white">
          <CardHeader>
            <div className="flex items-start justify-between mb-4">
              <div>
                <CardTitle className="text-4xl mb-4">{program.name}</CardTitle>
                <CardDescription className="text-cyan-300 text-lg">
                  {program.short_description}
                </CardDescription>
              </div>
              <div className="text-right">
                <div className="text-4xl font-bold text-cyan-400">€{program.price_euro.toFixed(2)}</div>
                <div className="text-sm text-gray-400">per person</div>
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-4 mt-6">
              <div className="bg-white/10 p-4 rounded-lg text-center">
                <div className="text-2xl font-bold text-cyan-400">{program.max_depth_meters}m</div>
                <div className="text-sm text-gray-400">Max Depth</div>
              </div>
              <div className="bg-white/10 p-4 rounded-lg text-center">
                <div className="text-2xl font-bold text-cyan-400">{program.min_age}+</div>
                <div className="text-sm text-gray-400">Minimum Age</div>
              </div>
              <div className="bg-white/10 p-4 rounded-lg text-center">
                <div className="text-2xl font-bold text-cyan-400">{program.duration.split(' ')[0]}</div>
                <div className="text-sm text-gray-400">Duration</div>
              </div>
            </div>
          </CardHeader>
          
          <CardContent className="space-y-6">
            <Separator className="bg-white/10" />
            
            <div>
              <h3 className="text-2xl font-bold mb-4">About This Program</h3>
              <p className="text-gray-300 leading-relaxed">{program.full_description}</p>
            </div>

            <Separator className="bg-white/10" />

            <div>
              <h3 className="text-2xl font-bold mb-4">What's Included</h3>
              <div className="grid md:grid-cols-2 gap-3">
                {program.includes.map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3 bg-white/5 p-3 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {program.name === "Discover Scuba Diving" && (
              <div className="bg-yellow-500/10 border border-yellow-500/30 p-4 rounded-lg">
                <p className="text-yellow-200 text-sm">
                  <strong>Safety Note:</strong> While the maximum depth for this program is 12 meters, for safety reasons we usually maintain a maximum depth of 6 meters during the dive.
                </p>
              </div>
            )}
          </CardContent>

          <CardFooter className="flex gap-4">
            <Button
              variant="outline"
              className="flex-1 border-white/20 text-white hover:bg-white/10"
              onClick={() => navigate('/packages')}
              data-testid="back-btn"
            >
              Back to Packages
            </Button>
            <Button
              className="flex-1 bg-cyan-500 hover:bg-cyan-600 text-white text-lg py-6"
              onClick={() => navigate('/contact')}
              data-testid="book-program-btn"
            >
              Book Now - €{program.price_euro.toFixed(2)}
            </Button>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};

// Inject theme CSS variables onto document body based on current route
const ThemeBodyInjector = () => {
  const location = useLocation();
  const { pageStyles } = useTheme();
  
  useEffect(() => {
    const path = location.pathname;
    let pageName = 'home';
    if (path === '/map') pageName = 'map';
    else if (path === '/dive-centers' || path.includes('/dive-center')) pageName = 'dive_centers';
    else if (path === '/packages') pageName = 'packages';
    else if (path === '/gallery') pageName = 'gallery';
    else if (path === '/reviews') pageName = 'reviews';
    else if (path === '/contact') pageName = 'contact';
    else if (path === '/' ) pageName = 'home';
    
    const isAdminPage = path.startsWith('/admin') || path.startsWith('/account');
    const theme = pageStyles[pageName]?.body;
    const root = document.documentElement;
    
    if (!theme || isAdminPage) {
      document.documentElement.setAttribute('data-theme', 'light');
      // Remove fouc-guard and restore light body for admin/light pages
      const foucGuard = document.getElementById('fouc-guard');
      if (foucGuard) foucGuard.remove();
      document.body.style.backgroundColor = '#ffffff';
      document.body.style.color = '#1e293b';
      root.style.setProperty('--page-bg', '#ffffff');
      root.style.setProperty('--page-text', '#1e293b');
      root.style.setProperty('--page-accent', '#0891b2');
      root.style.setProperty('--page-accent-secondary', '#0891b2');
      root.style.setProperty('--page-border', 'rgba(229,231,235,1)');
      root.style.setProperty('--page-card-bg', '#ffffff');
      root.style.setProperty('--page-card-bg-hover', '#f9fafb');
      root.style.setProperty('--page-muted', '#f1f5f9');
      root.style.setProperty('--page-muted-text', '#64748b');
      root.style.setProperty('--page-heading', '#0f172a');
      root.style.setProperty('--page-subtle', '#94a3b8');
      root.style.setProperty('--page-input-bg', '#ffffff');
      root.style.setProperty('--page-input-border', '#d1d5db');
      root.style.setProperty('--page-shadow', '0 1px 3px rgba(0,0,0,0.1)');
      try { localStorage.setItem('gd_theme_cache', JSON.stringify({ isDark: false })); } catch(e) {}
      return;
    }
    
    const bgValue = theme.background_value || '#ffffff';
    const textColor = theme.text_color || '#1e293b';
    const accent = theme.accent_color || '#0891b2';
    const accentSec = theme.accent_color_secondary || accent;
    const border = theme.border_color || 'rgba(229,231,235,1)';
    const isDark = textColor === '#e2e8f0' || textColor === '#ffffff' || textColor === '#f8fafc';
    
    // Apply data-theme on <html> (documentElement) so the FOUC script can
    // synchronously set the same attribute before React mounts.
    document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
    
    root.style.setProperty('--page-bg', bgValue);
    root.style.setProperty('--page-text', textColor);
    root.style.setProperty('--page-accent', accent);
    root.style.setProperty('--page-accent-secondary', accentSec);
    root.style.setProperty('--page-border', border);
    root.style.setProperty('--page-card-bg', isDark ? '#1c1533' : '#ffffff');
    root.style.setProperty('--page-card-bg-hover', isDark ? '#241c3e' : '#f9fafb');
    root.style.setProperty('--page-muted', isDark ? '#161025' : '#f1f5f9');
    root.style.setProperty('--page-muted-text', isDark ? '#94a3b8' : '#64748b');
    root.style.setProperty('--page-heading', isDark ? '#f1f5f9' : '#0f172a');
    root.style.setProperty('--page-subtle', isDark ? '#64748b' : '#94a3b8');
    root.style.setProperty('--page-input-bg', isDark ? '#15101e' : '#ffffff');
    root.style.setProperty('--page-input-border', isDark ? 'rgba(162,133,212,0.2)' : '#d1d5db');
    root.style.setProperty('--page-shadow', isDark ? 'none' : '0 1px 3px rgba(0,0,0,0.1)');
    
    // For dark themes, let the CSS html[data-theme="dark"] rule handle bg (with tentacle pattern + animation)
    // For light themes, set inline since there's no pattern
    if (!isDark) {
      document.body.style.backgroundColor = bgValue;
    } else {
      document.body.style.backgroundColor = '';
    }
    document.body.style.color = textColor;
    
    // Cache theme for FOUC prevention on next page load
    try {
      localStorage.setItem('gd_theme_cache', JSON.stringify({ isDark, bg: bgValue, text: textColor, accent, accentSec }));
    } catch(e) {}
    
    // Remove the FOUC guard style injected by index.html now that React has taken over
    const foucGuard = document.getElementById('fouc-guard');
    if (foucGuard) foucGuard.remove();
    document.documentElement.removeAttribute('data-fouc-dark');
    
    return () => {
      document.documentElement.removeAttribute('data-theme');
      document.body.style.backgroundColor = '';
      document.body.style.color = '';
    };
  }, [location.pathname, pageStyles]);
  
  return null;
};

function App() {
  // Prevent body style changes from Radix UI
  usePreventBodyStyleChanges();
  
  // Initialize push notifications for logged-in users
  usePushNotifications();
  
  // Listen for SW update available
  useEffect(() => {
    const handleUpdate = (e) => {
      const { registration } = e.detail;
      const { toast } = require('sonner');
      toast('New version available!', {
        description: 'Click to update to the latest version.',
        duration: Infinity,
        action: {
          label: 'Update',
          onClick: () => {
            if (registration.waiting) {
              registration.waiting.postMessage({ type: 'SKIP_WAITING' });
            }
          }
        }
      });
    };
    window.addEventListener('swUpdateAvailable', handleUpdate);
    return () => window.removeEventListener('swUpdateAvailable', handleUpdate);
  }, []);
  
  return (
    <ThemeProvider>
      <div className="App">
        <Toaster position="top-center" richColors />
        <BrowserRouter>
          <ScrollToTop />
          <ThemeBodyInjector />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/packages" element={<Packages />} />
            <Route path="/map" element={<Map />} />
            <Route path="/dive-centers" element={<PartnerDiveCenters />} />
            <Route path="/program/:programId" element={<ProgramDetail />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="/reviews" element={<Reviews />} />
            <Route path="/reviews/submit" element={<Navigate to="/submit-review" replace />} />
            <Route path="/review" element={<ReviewRedirect />} />
            <Route path="/account" element={<Account />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/register" element={<Register />} />
            <Route path="/request-invite" element={<RequestInvite />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/profile/:userId" element={<Profile />} />
            <Route path="/submit-review" element={<SubmitReview />} />
            <Route path="/submit-dive-site-report" element={<SubmitDiveSiteReport />} />
            <Route path="/dive-report" element={<DiveReportForm />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/privacy-settings" element={<PrivacySettings />} />
            <Route path="/accessibility" element={<Accessibility />} />
            <Route path="/community" element={<Community />} />
            <Route path="/community/pros" element={<ProsDirectory />} />
            <Route path="/community/dive-centers" element={<DiveCentersDirectory />} />
            <Route path="/community/forum" element={<Forum />} />
            <Route path="/community/forum/:threadId" element={<ThreadDetail />} />
            <Route path="/community/marine-database" element={<MarineDatabase />} />
          </Routes>
          <GlobalFloatingChat />
          <NewUserWalkthrough />
          <CookieConsent />
        </BrowserRouter>
      </div>
    </ThemeProvider>
  );
}

export default App;
